import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, User, ChevronDown, Shield } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const linkClass = ({ isActive }) =>
  `text-sm font-medium tracking-wide transition-colors ${
    isActive ? "text-[color:var(--azsam-clay)]" : "text-[color:var(--azsam-ink)] hover:text-[color:var(--azsam-clay)]"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[color:var(--azsam-sand)]/85 border-b border-[color:var(--azsam-stone)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-brand">
          <div className="w-10 h-10 rounded-full bg-[color:var(--azsam-clay)] text-white flex items-center justify-center font-heading text-lg font-semibold">
            A
          </div>
          <div className="leading-tight">
            <div className="font-heading text-xl leading-none">AzSAM</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--azsam-ink-soft)]">
              AZ Society of Addiction Medicine
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <NavLink to="/" end className={linkClass} data-testid="nav-home">Home</NavLink>
          <NavLink to="/about" className={linkClass} data-testid="nav-about">About</NavLink>
          <NavLink to="/events" className={linkClass} data-testid="nav-events">Events</NavLink>
          <NavLink to="/resources" className={linkClass} data-testid="nav-resources">Resources</NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium hover:text-[color:var(--azsam-clay)] outline-none" data-testid="nav-community-trigger">
              Community <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuItem asChild>
                <Link to="/news" data-testid="nav-news">AzSAM Initiatives</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/committees" data-testid="nav-committees">Committees</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/solicit-support" data-testid="nav-solicit-support">How Can We Help You?</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/contact" className={linkClass} data-testid="nav-contact">Contact</NavLink>
          {user && <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">Portal</NavLink>}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
              <span className="inline-flex items-center gap-1"><Shield size={13} /> Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="flex items-center gap-2 text-sm text-[color:var(--azsam-ink-soft)] hover:text-[color:var(--azsam-clay)]" data-testid="nav-user">
                <User size={16} /> {user.name.split(" ")[0]}
              </Link>
              <button onClick={handleLogout} className="btn-outline !py-2 !px-4 text-sm" data-testid="nav-logout">
                <LogOut size={14} /> Logout
              </button>
            </div>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium hover:text-[color:var(--azsam-clay)]" data-testid="nav-login">Log in</Link>
              <Link to="/register" className="btn-primary !py-2.5 text-sm" data-testid="nav-register">Join AzSAM</Link>
            </>
          )}
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[color:var(--azsam-stone)] bg-[color:var(--azsam-sand)]" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            <NavLink to="/" end className={linkClass} onClick={() => setOpen(false)}>Home</NavLink>
            <NavLink to="/about" className={linkClass} onClick={() => setOpen(false)}>About</NavLink>
            <NavLink to="/events" className={linkClass} onClick={() => setOpen(false)}>Events</NavLink>
            <NavLink to="/resources" className={linkClass} onClick={() => setOpen(false)}>Resources</NavLink>
            <NavLink to="/news" className={linkClass} onClick={() => setOpen(false)}>AzSAM Initiatives</NavLink>
            <NavLink to="/committees" className={linkClass} onClick={() => setOpen(false)}>Committees</NavLink>
            <NavLink to="/solicit-support" className={linkClass} onClick={() => setOpen(false)}>How Can We Help You?</NavLink>
            <NavLink to="/contact" className={linkClass} onClick={() => setOpen(false)}>Contact</NavLink>
            {user ? (
              <>
                <NavLink to="/dashboard" className={linkClass} onClick={() => setOpen(false)}>Portal</NavLink>
                {user.role === "admin" && <NavLink to="/admin" className={linkClass} onClick={() => setOpen(false)}>Admin</NavLink>}
                <button onClick={handleLogout} className="btn-outline w-fit !py-2 !px-4 text-sm">
                  <LogOut size={14} /> Logout
                </button>
              </>
            ) : (
              <div className="flex gap-3">
                <Link to="/login" className="btn-outline !py-2 !px-4 text-sm" onClick={() => setOpen(false)}>Log in</Link>
                <Link to="/register" className="btn-primary !py-2 !px-4 text-sm" onClick={() => setOpen(false)}>Join AzSAM</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
